import React, { useState, useCallback, useEffect } from 'react';
import { billingAdminService } from '../services/api';
import {
  Landmark, CreditCard, RefreshCw, ChevronLeft, ChevronRight,
  ArrowUpDown, ArrowUp, ArrowDown, X,
  CheckCircle, XCircle, Clock,
  Activity, Copy, Webhook, RotateCcw, Eye, AlertTriangle,
} from 'lucide-react';
import './BillingManagement.css';
import './BillingPages.css';

const fmt = (v) => Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });
const fmtINR = (v) => `₹${fmt(v)}`;
const copyId = (text) => { navigator.clipboard.writeText(text).catch(() => {}); };

const relativeDate = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now - d;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: d.getFullYear() !== now.getFullYear() ? 'numeric' : undefined });
};

const fullDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const StatusBadge = ({ status }) => {
  const cfg = {
    active: 'status-active', expired: 'status-disabled', failed: 'status-disabled',
    removed: 'status-muted', pending: 'status-pending',
  };
  return <span className={`status-badge ${cfg[status] || ''}`}>{status}</span>;
};

const MethodTypeBadge = ({ type }) => {
  const labels = {
    upi_autopay: 'UPI Autopay', enach: 'eNACH', card: 'Card',
    netbanking: 'Net Banking', wallet: 'Wallet', bank_transfer: 'Bank Transfer',
  };
  return (
    <span className="billing-mode-badge billing-mode--threshold">
      {labels[type] || type}
    </span>
  );
};

// ── Activity Feed Section ─────────────────────────────────────
const ActivityFeed = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchFeed = useCallback(async () => {
    try {
      setLoading(true);
      const data = await billingAdminService.getActivityFeed({ limit: 30 });
      setEvents(data.events || []);
    } catch (err) { console.error('Failed to load feed:', err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchFeed(); }, [fetchFeed]);

  // Auto-refresh every 30s
  useEffect(() => {
    const interval = setInterval(fetchFeed, 30000);
    return () => clearInterval(interval);
  }, [fetchFeed]);

  const getEventDotColor = (actionType) => {
    if (['charge_success', 'mandate_approved', 'campaigns_resumed'].includes(actionType)) return 'dot-success';
    if (['charge_failed', 'spending_limit_reached', 'campaigns_paused'].includes(actionType)) return 'dot-danger';
    if (['charge_initiated', 'charge_notified'].includes(actionType)) return 'dot-info';
    if (['credit_granted', 'credit_applied'].includes(actionType)) return 'dot-purple';
    if (['account_created', 'mandate_registered'].includes(actionType)) return 'dot-warning';
    return 'dot-default';
  };

  return (
    <div className="billing-card" style={{ padding: '1.25rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
        <h3 style={{ margin: 0, fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Activity size={16} /> Recent Activity
        </h3>
        <button className="btn btn-secondary btn-sm" onClick={fetchFeed} title="Refresh">
          <RefreshCw size={12} />
        </button>
      </div>
      {loading ? (
        <div className="loading"><div className="spinner" /></div>
      ) : events.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '1.5rem', color: '#999', fontSize: '0.9rem' }}>
          No billing events yet
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', maxHeight: 400, overflowY: 'auto' }}>
          {events.map(ev => (
            <div key={ev.id} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', padding: '0.5rem 0', borderBottom: '1px solid #f5f5f5' }}>
              <span className={`event-dot ${getEventDotColor(ev.action_type)}`} style={{ marginTop: 5 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '0.85rem', color: '#333' }}>
                  <strong>{ev.tenant_name || 'System'}</strong>{' — '}
                  {ev.description || ev.action_type?.replace(/_/g, ' ')}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#999', display: 'flex', gap: '0.75rem', marginTop: 2 }}>
                  <span>{relativeDate(ev.created_at)}</span>
                  {ev.actor_email && <span>{ev.actor_email}</span>}
                  {!ev.actor_email && ev.actor_type === 'system' && <span>system</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Payment Methods Tab ───────────────────────────────────────
const PaymentMethodsTab = () => {
  const [methods, setMethods] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 25, totalCount: 0, totalPages: 0 });
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [sorting, setSorting] = useState({ sortBy: 'created_at', sortOrder: 'desc' });
  const [selectedPM, setSelectedPM] = useState(null);

  const fetchMethods = useCallback(async () => {
    try {
      setLoading(true);
      const data = await billingAdminService.getPaymentMethods({
        page: pagination.page, pageSize: pagination.pageSize,
        sortBy: sorting.sortBy, sortOrder: sorting.sortOrder,
        ...(search && { search }), ...(statusFilter && { status: statusFilter }),
        ...(typeFilter && { method_type: typeFilter }),
      });
      setMethods(data.paymentMethods || []);
      setSummary(data.summary || null);
      if (data.pagination) setPagination(prev => ({ ...prev, ...data.pagination }));
    } catch (err) { console.error('Failed to load payment methods:', err); }
    finally { setLoading(false); }
  }, [pagination.page, pagination.pageSize, search, statusFilter, typeFilter, sorting]);

  useEffect(() => { fetchMethods(); }, [fetchMethods]);

  const handleSort = (field) => {
    setSorting(prev => ({ sortBy: field, sortOrder: prev.sortBy === field && prev.sortOrder === 'asc' ? 'desc' : 'asc' }));
  };
  const getSortIcon = (field) => {
    if (sorting.sortBy !== field) return <ArrowUpDown size={12} className="sort-icon" />;
    return sorting.sortOrder === 'asc' ? <ArrowUp size={12} className="sort-icon active" /> : <ArrowDown size={12} className="sort-icon active" />;
  };

  const showStart = Math.min(((pagination.page - 1) * pagination.pageSize) + 1, pagination.totalCount);
  const showEnd = Math.min(pagination.page * pagination.pageSize, pagination.totalCount);

  return (
    <>
      {/* Summary */}
      {summary && (
        <div className="billing-summary-cards">
          <div className="billing-summary-card">
            <h4><CreditCard size={13} /> Total Mandates</h4>
            <div className="summary-value">{summary.total}</div>
          </div>
          <div className="billing-summary-card card-success">
            <h4><CheckCircle size={13} /> Active</h4>
            <div className="summary-value">{summary.active}</div>
          </div>
          <div className="billing-summary-card card-danger">
            <h4><XCircle size={13} /> Failed / Expired</h4>
            <div className="summary-value">{summary.failed_expired}</div>
          </div>
          <div className="billing-summary-card">
            <h4><Clock size={13} /> Removed</h4>
            <div className="summary-value">{summary.removed}</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="billing-filters">
        <input type="text" className="search-input" placeholder="Search by tenant or UPI ID…"
          value={search} onChange={e => { setSearch(e.target.value); setPagination(p => ({ ...p, page: 1 })); }} />
        <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPagination(p => ({ ...p, page: 1 })); }}>
          <option value="">All Statuses</option>
          <option value="active">Active</option>
          <option value="failed">Failed</option>
          <option value="expired">Expired</option>
          <option value="removed">Removed</option>
        </select>
        <select value={typeFilter} onChange={e => { setTypeFilter(e.target.value); setPagination(p => ({ ...p, page: 1 })); }}>
          <option value="">All Types</option>
          <option value="upi_autopay">UPI Autopay</option>
          <option value="enach">eNACH</option>
          <option value="card">Card</option>
          <option value="netbanking">Net Banking</option>
        </select>
        <button className="btn btn-secondary btn-sm" onClick={fetchMethods} title="Refresh"><RefreshCw size={14} /></button>
      </div>

      {/* Table */}
      {loading ? (
        <div className="loading"><div className="spinner" /></div>
      ) : methods.length === 0 ? (
        <div className="billing-card">
          <div className="billing-empty">
            <CreditCard size={40} />
            <p>No payment methods found</p>
            <span>Mandates appear when tenants register UPI Autopay or eNACH</span>
          </div>
        </div>
      ) : (
        <>
          <div className="data-table">
            <table>
              <thead>
                <tr>
                  <th>Tenant</th>
                  <th>Method</th>
                  <th>Type</th>
                  <th>Level</th>
                  <th className="sortable" onClick={() => handleSort('max_amount')}>Max Amount {getSortIcon('max_amount')}</th>
                  <th className="sortable" onClick={() => handleSort('status')}>Status {getSortIcon('status')}</th>
                  <th>Primary</th>
                  <th>Provider</th>
                  <th className="sortable" onClick={() => handleSort('created_at')}>Registered {getSortIcon('created_at')}</th>
                </tr>
              </thead>
              <tbody>
                {methods.map(pm => (
                  <tr key={pm.id} className={`clickable-row ${pm.status === 'failed' ? 'row-failed' : ''}`}
                    onClick={() => setSelectedPM(pm)}>
                    <td>
                      <div className="user-cell">
                        <div className="user-avatar">{(pm.tenant_name || '?').charAt(0)}</div>
                        <span className="user-name">{pm.tenant_name || 'Unknown'}</span>
                      </div>
                    </td>
                    <td>
                      <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>{pm.display_label || '—'}</span>
                    </td>
                    <td><MethodTypeBadge type={pm.mandate_type || pm.method_type} /></td>
                    <td>L{pm.threshold_level || '—'}</td>
                    <td><span className="amount-cell">{pm.max_amount ? fmtINR(pm.max_amount) : '—'}</span></td>
                    <td><StatusBadge status={pm.status} /></td>
                    <td>{pm.is_primary ? <CheckCircle size={14} style={{ color: '#27ae60' }} /> : <span className="text-muted">—</span>}</td>
                    <td style={{ fontSize: '0.85rem' }}>{pm.provider || '—'}</td>
                    <td className="text-muted">{relativeDate(pm.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pagination.totalPages > 0 && (
            <div className="pagination">
              <span>Showing {showStart}–{showEnd} of {pagination.totalCount} methods</span>
              <div className="pagination-buttons">
                <button className="btn btn-secondary btn-sm" disabled={pagination.page <= 1}
                  onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}>
                  <ChevronLeft size={14} /> Prev
                </button>
                <button className="btn btn-secondary btn-sm" disabled={pagination.page >= pagination.totalPages}
                  onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}>
                  Next <ChevronRight size={14} />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Detail drawer */}
      {selectedPM && (
        <>
          <div className="detail-drawer-backdrop" onClick={() => setSelectedPM(null)} />
          <div className="detail-drawer">
            <div className="detail-drawer-header">
              <h2><CreditCard size={18} /> Payment Method</h2>
              <button className="btn btn-secondary btn-sm" onClick={() => setSelectedPM(null)}><X size={14} /></button>
            </div>
            <div className="detail-drawer-body">
              <div className="detail-drawer-section">
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
                  <span style={{ fontSize: '1.15rem', fontWeight: 700 }}>{selectedPM.display_label || 'Payment Method'}</span>
                  <StatusBadge status={selectedPM.status} />
                </div>
              </div>

              <div className="detail-drawer-section">
                <h3>Details</h3>
                <div className="drawer-detail-grid">
                  <div className="drawer-detail-item">
                    <label>Tenant</label>
                    <span>{selectedPM.tenant_name || '—'}</span>
                  </div>
                  <div className="drawer-detail-item">
                    <label>Type</label>
                    <span><MethodTypeBadge type={selectedPM.mandate_type || selectedPM.method_type} /></span>
                  </div>
                  <div className="drawer-detail-item">
                    <label>Provider</label>
                    <span>{selectedPM.provider || '—'}</span>
                  </div>
                  <div className="drawer-detail-item">
                    <label>Max Amount</label>
                    <span>{selectedPM.max_amount ? fmtINR(selectedPM.max_amount) : '—'}</span>
                  </div>
                  <div className="drawer-detail-item">
                    <label>Threshold Level</label>
                    <span>L{selectedPM.threshold_level || '—'}</span>
                  </div>
                  <div className="drawer-detail-item">
                    <label>Primary</label>
                    <span>{selectedPM.is_primary ? 'Yes' : 'No'}</span>
                  </div>
                  <div className="drawer-detail-item">
                    <label>Registered</label>
                    <span>{fullDate(selectedPM.created_at)}</span>
                  </div>
                  <div className="drawer-detail-item">
                    <label>Updated</label>
                    <span>{fullDate(selectedPM.updated_at)}</span>
                  </div>
                </div>
              </div>

              {/* Provider refs */}
              <div className="detail-drawer-section">
                <h3>Provider References</h3>
                <div className="drawer-detail-grid">
                  {selectedPM.provider_mandate_id && (
                    <div className="drawer-detail-item full-width">
                      <label>Provider Mandate ID</label>
                      <span style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                        {selectedPM.provider_mandate_id}
                        <button className="copy-btn" onClick={() => copyId(selectedPM.provider_mandate_id)} title="Copy" style={{ marginLeft: 4 }}><Copy size={10} /></button>
                      </span>
                    </div>
                  )}
                  <div className="drawer-detail-item full-width">
                    <label>Payment Method ID</label>
                    <span style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                      {selectedPM.id}
                      <button className="copy-btn" onClick={() => copyId(selectedPM.id)} title="Copy" style={{ marginLeft: 4 }}><Copy size={10} /></button>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

// ── Webhook Logs Tab ──────────────────────────────────────────
const WebhookLogsTab = () => {
  const [webhooks, setWebhooks] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 25, totalCount: 0, totalPages: 0 });
  const [processedFilter, setProcessedFilter] = useState('');
  const [eventTypeFilter, setEventTypeFilter] = useState('');
  const [sorting] = useState({ sortOrder: 'desc' });
  const [selectedWebhook, setSelectedWebhook] = useState(null);
  const [webhookDetail, setWebhookDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [reprocessing, setReprocessing] = useState(null);
  const [actionMessage, setActionMessage] = useState(null);

  const fetchWebhooks = useCallback(async () => {
    try {
      setLoading(true);
      const data = await billingAdminService.getWebhooks({
        page: pagination.page, pageSize: pagination.pageSize,
        sortOrder: sorting.sortOrder,
        ...(processedFilter !== '' && { processed: processedFilter }),
        ...(eventTypeFilter && { event_type: eventTypeFilter }),
      });
      setWebhooks(data.webhooks || []);
      setSummary(data.summary || null);
      if (data.pagination) setPagination(prev => ({ ...prev, ...data.pagination }));
    } catch (err) { console.error('Failed to load webhooks:', err); }
    finally { setLoading(false); }
  }, [pagination.page, pagination.pageSize, processedFilter, eventTypeFilter, sorting]);

  useEffect(() => { fetchWebhooks(); }, [fetchWebhooks]);

  const handleViewDetail = async (wh) => {
    setSelectedWebhook(wh);
    setDetailLoading(true);
    try {
      const data = await billingAdminService.getWebhook(wh.id);
      setWebhookDetail(data.webhook);
    } catch { setWebhookDetail(null); }
    finally { setDetailLoading(false); }
  };

  const handleReprocess = async (webhookId) => {
    setReprocessing(webhookId); setActionMessage(null);
    try {
      await billingAdminService.reprocessWebhook(webhookId);
      setActionMessage({ type: 'success', text: 'Webhook queued for reprocessing' });
      fetchWebhooks();
      if (selectedWebhook?.id === webhookId) setSelectedWebhook(null);
    } catch (err) {
      setActionMessage({ type: 'error', text: err.response?.data?.error || 'Reprocess failed' });
    } finally { setReprocessing(null); }
  };

  const showStart = Math.min(((pagination.page - 1) * pagination.pageSize) + 1, pagination.totalCount);
  const showEnd = Math.min(pagination.page * pagination.pageSize, pagination.totalCount);

  return (
    <>
      {/* Summary */}
      {summary && (
        <div className="billing-summary-cards">
          <div className="billing-summary-card">
            <h4><Webhook size={13} /> Total</h4>
            <div className="summary-value">{summary.total}</div>
            <div className="summary-sub">webhooks received</div>
          </div>
          <div className="billing-summary-card card-success">
            <h4><CheckCircle size={13} /> Processed</h4>
            <div className="summary-value">{summary.processed}</div>
            <div className="summary-sub">successfully handled</div>
          </div>
          <div className="billing-summary-card card-danger">
            <h4><XCircle size={13} /> Failed</h4>
            <div className="summary-value">{summary.failed}</div>
            <div className="summary-sub">{summary.failed > 0 ? 'needs attention' : 'all clear'}</div>
          </div>
        </div>
      )}

      {actionMessage && (
        <div className={`alert ${actionMessage.type === 'success' ? 'alert-success' : 'alert-error'}`} style={{ marginBottom: '1rem' }}>
          {actionMessage.text}
          <button style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer' }} onClick={() => setActionMessage(null)}><X size={14} /></button>
        </div>
      )}

      {/* Filters */}
      <div className="billing-filters">
        <select value={processedFilter} onChange={e => { setProcessedFilter(e.target.value); setPagination(p => ({ ...p, page: 1 })); }}>
          <option value="">All Status</option>
          <option value="true">Processed</option>
          <option value="false">Failed / Pending</option>
        </select>
        <select value={eventTypeFilter} onChange={e => { setEventTypeFilter(e.target.value); setPagination(p => ({ ...p, page: 1 })); }}>
          <option value="">All Event Types</option>
          <option value="mandate">Mandate Events</option>
          <option value="transaction">Transaction Events</option>
          <option value="payment">Payment Events</option>
          <option value="debit">Debit Events</option>
        </select>
        <button className="btn btn-secondary btn-sm" onClick={fetchWebhooks} title="Refresh"><RefreshCw size={14} /></button>
      </div>

      {/* Table */}
      {loading ? (
        <div className="loading"><div className="spinner" /></div>
      ) : webhooks.length === 0 ? (
        <div className="billing-card">
          <div className="billing-empty">
            <Webhook size={40} />
            <p>No webhook logs found</p>
            <span>Webhooks appear when Easebuzz sends payment status callbacks</span>
          </div>
        </div>
      ) : (
        <>
          <div className="data-table">
            <table>
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Provider</th>
                  <th>Event Type</th>
                  <th>Provider Event ID</th>
                  <th>Payload Size</th>
                  <th>Received</th>
                  <th>Processed At</th>
                  <th>Error</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {webhooks.map(wh => (
                  <tr key={wh.id} className={!wh.processed && wh.processing_error ? 'row-failed' : ''}>
                    <td>
                      {wh.processed
                        ? <span className="status-badge status-active">Processed</span>
                        : <span className="status-badge status-disabled">Failed</span>}
                    </td>
                    <td style={{ fontSize: '0.85rem' }}>{wh.provider}</td>
                    <td><code style={{ fontSize: '0.8rem', background: '#f5f5f5', padding: '2px 6px', borderRadius: 4 }}>{wh.event_type}</code></td>
                    <td style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{wh.provider_event_id ? wh.provider_event_id.slice(0, 16) + '…' : '—'}</td>
                    <td className="text-muted" style={{ fontSize: '0.85rem' }}>{wh.payload_size ? `${(wh.payload_size / 1024).toFixed(1)} KB` : '—'}</td>
                    <td className="text-muted">{relativeDate(wh.received_at)}</td>
                    <td className="text-muted">{wh.processed_at ? relativeDate(wh.processed_at) : '—'}</td>
                    <td>
                      {wh.processing_error
                        ? <span className="failure-text" title={wh.processing_error}><XCircle size={11} /> {wh.processing_error.slice(0, 30)}…</span>
                        : <span className="text-muted">—</span>}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '0.25rem' }}>
                        <button className="btn btn-secondary btn-sm" onClick={() => handleViewDetail(wh)} title="View Payload">
                          <Eye size={12} />
                        </button>
                        {!wh.processed && (
                          <button className="btn btn-secondary btn-sm" onClick={() => handleReprocess(wh.id)}
                            disabled={reprocessing === wh.id} title="Reprocess"
                            style={{ borderColor: '#f39c12', color: '#f39c12' }}>
                            {reprocessing === wh.id ? <div className="btn-spinner" /> : <RotateCcw size={12} />}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pagination.totalPages > 0 && (
            <div className="pagination">
              <span>Showing {showStart}–{showEnd} of {pagination.totalCount} webhooks</span>
              <div className="pagination-buttons">
                <button className="btn btn-secondary btn-sm" disabled={pagination.page <= 1}
                  onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}>
                  <ChevronLeft size={14} /> Prev
                </button>
                <button className="btn btn-secondary btn-sm" disabled={pagination.page >= pagination.totalPages}
                  onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}>
                  Next <ChevronRight size={14} />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Webhook detail drawer */}
      {selectedWebhook && (
        <>
          <div className="detail-drawer-backdrop" onClick={() => setSelectedWebhook(null)} />
          <div className="detail-drawer">
            <div className="detail-drawer-header">
              <h2><Webhook size={18} /> Webhook Detail</h2>
              <button className="btn btn-secondary btn-sm" onClick={() => setSelectedWebhook(null)}><X size={14} /></button>
            </div>
            <div className="detail-drawer-body">
              <div className="detail-drawer-section">
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
                  <code style={{ fontSize: '1rem', fontWeight: 600 }}>{selectedWebhook.event_type}</code>
                  {selectedWebhook.processed
                    ? <span className="status-badge status-active">Processed</span>
                    : <span className="status-badge status-disabled">Failed</span>}
                </div>
              </div>

              <div className="detail-drawer-section">
                <h3>Info</h3>
                <div className="drawer-detail-grid">
                  <div className="drawer-detail-item">
                    <label>Provider</label><span>{selectedWebhook.provider}</span>
                  </div>
                  <div className="drawer-detail-item">
                    <label>Received</label><span>{fullDate(selectedWebhook.received_at)}</span>
                  </div>
                  <div className="drawer-detail-item">
                    <label>Processed At</label><span>{selectedWebhook.processed_at ? fullDate(selectedWebhook.processed_at) : '—'}</span>
                  </div>
                  <div className="drawer-detail-item">
                    <label>Provider Event ID</label>
                    <span style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                      {selectedWebhook.provider_event_id || '—'}
                      {selectedWebhook.provider_event_id && <button className="copy-btn" onClick={() => copyId(selectedWebhook.provider_event_id)} style={{ marginLeft: 4 }}><Copy size={10} /></button>}
                    </span>
                  </div>
                </div>
              </div>

              {selectedWebhook.processing_error && (
                <div className="detail-drawer-section">
                  <h3>Error</h3>
                  <div style={{ background: '#fff5f5', border: '1px solid #fecaca', borderRadius: 8, padding: '0.75rem', color: '#dc3545', fontSize: '0.85rem' }}>
                    <AlertTriangle size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                    {selectedWebhook.processing_error}
                  </div>
                </div>
              )}

              <div className="detail-drawer-section">
                <h3>Payload</h3>
                {detailLoading ? (
                  <div className="loading"><div className="spinner" /></div>
                ) : webhookDetail?.payload ? (
                  <div className="drawer-json">{JSON.stringify(webhookDetail.payload, null, 2)}</div>
                ) : (
                  <div className="text-muted" style={{ fontSize: '0.85rem' }}>No payload data</div>
                )}
              </div>

              {/* Actions */}
              {!selectedWebhook.processed && (
                <div className="detail-drawer-section">
                  <h3>Actions</h3>
                  <button className="btn btn-primary btn-sm" onClick={() => handleReprocess(selectedWebhook.id)}
                    disabled={reprocessing === selectedWebhook.id}>
                    {reprocessing === selectedWebhook.id ? <><div className="btn-spinner" /> Reprocessing…</> : <><RotateCcw size={13} /> Reprocess Webhook</>}
                  </button>
                </div>
              )}

              {/* IDs */}
              <div className="detail-drawer-section">
                <h3>Reference IDs</h3>
                <div className="drawer-detail-grid">
                  <div className="drawer-detail-item full-width">
                    <label>Webhook ID</label>
                    <span style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                      {selectedWebhook.id}
                      <button className="copy-btn" onClick={() => copyId(selectedWebhook.id)} style={{ marginLeft: 4 }}><Copy size={10} /></button>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

// ── Main Page ─────────────────────────────────────────────────
const BillingGateway = () => {
  const [tab, setTab] = useState('methods');

  return (
    <div className="billing-management">
      <div className="billing-page-header">
        <h1><Landmark size={22} /> Payment Gateway</h1>
      </div>

      {/* Tab bar */}
      <div className="billing-tab-bar">
        <button className={`billing-tab-btn ${tab === 'methods' ? 'active' : ''}`} onClick={() => setTab('methods')}>
          <CreditCard size={14} /> Payment Methods
        </button>
        <button className={`billing-tab-btn ${tab === 'webhooks' ? 'active' : ''}`} onClick={() => setTab('webhooks')}>
          <Webhook size={14} /> Webhook Logs
        </button>
        <button className={`billing-tab-btn ${tab === 'activity' ? 'active' : ''}`} onClick={() => setTab('activity')}>
          <Activity size={14} /> Activity Feed
        </button>
      </div>

      {tab === 'methods' && <PaymentMethodsTab />}
      {tab === 'webhooks' && <WebhookLogsTab />}
      {tab === 'activity' && <ActivityFeed />}
    </div>
  );
};

export default BillingGateway;
